// Discovery Logging Module
// Handles all discovery-related logging functionality

import type { TimezoneConfig } from '../canister-client';
import { windowFunctionRegistry } from '../core/window-function-registry';

/**
 * Type definition for log message levels
 */
type LogLevel = 'info' | 'success' | 'warning' | 'error' | 'detail' | 'progress' | 'phase';

/**
 * Type definition for console methods
 */
type ConsoleMethod = 'log' | 'warn' | 'error';

/**
 * Interface for device discovery result
 */
interface DeviceResult {
    status: 'discovered' | 'offline' | 'unreachable';
    inventory?: {
        hostname?: string;
        accessible?: boolean;
        method?: string;
    };
}

/**
 * Interface for extending the Window object with global functions and properties
 */
declare global {
    interface Window {
        DiscoveryLogger: typeof DiscoveryLogger;
        discoveryLogger?: DiscoveryLogger;
        appCore?: {
            getModule(name: string): DiscoveryLogger | undefined;
        };
        logDiscovery: (message: string, type?: LogLevel) => void;
        clearDiscoveryLog: () => void;
        downloadDiscoveryLog: () => void;
    }
}

/**
 * DiscoveryLogger class provides comprehensive logging functionality for the discovery process.
 *
 * Features:
 * - Timestamped log entries with color-coded severity levels
 * - Log persistence and export functionality
 * - Progress tracking for batch operations
 * - Device-specific result logging
 * - Console integration for debugging
 * - Dynamic timezone configuration via centralized Timezone Configuration Service
 *
 * Architecture:
 * - Follows SOA principles by consuming centralized Timezone Configuration Service
 * - Caches timezone config locally to minimize service calls
 * - Automatically fetches timezone on first use
 * - Provides refresh mechanism for settings updates
 *
 * @class DiscoveryLogger
 */
export class DiscoveryLogger {
    private logElement: HTMLElement | null;
    private logContainer: HTMLElement | null;

    // Timezone Configuration Cache
    // Following SOA principles - cache timezone config to minimize service calls
    // Initialized to null, fetched on first use
    private static timezoneCache: TimezoneConfig | null = null;

    constructor() {
        this.logElement = null;
        this.logContainer = null;
        this.initializeElements();
        this.initializeTimezone();
    }

    /**
     * Initializes DOM element references for the log display
     * @private
     */
    private initializeElements(): void {
        this.logElement = document.getElementById('discovery-log');
        this.logContainer = document.getElementById('discovery-log-container');

        if (!this.logElement) {
            console.warn('⚠️ Discovery log element not found');
        }
        if (!this.logContainer) {
            console.warn('⚠️ Discovery log container not found');
        }
    }

    /**
     * Initializes timezone configuration by fetching from service
     * Following SOA principles - fetches from centralized Timezone Configuration Service
     * Caches result to minimize future service calls
     * @private
     */
    private async initializeTimezone(): Promise<void> {
        // Only fetch if cache is empty
        if (DiscoveryLogger.timezoneCache !== null) {
            return;
        }

        try {
            // Access global canisterClient instance
            if (typeof window !== 'undefined' && window.canisterClient) {
                const response = await window.canisterClient.getTimezoneConfig();

                // Parse from raw Candid string if data is empty (same logic as HTML download)
                let offsetHours, label, description;
                if (response.raw) {
                    // Extract values from Candid string
                    const offsetMatch = response.raw.match(/utc_offset_hours\s*=\s*(-?\d+)/);
                    const labelMatch = response.raw.match(/label\s*=\s*"([^"]+)"/);
                    const descMatch = response.raw.match(/description\s*=\s*"([^"]+)"/);

                    if (offsetMatch && labelMatch) {
                        offsetHours = parseInt(offsetMatch[1], 10);
                        label = labelMatch[1];
                        description = descMatch ? descMatch[1] : `${label} timezone`;

                        DiscoveryLogger.timezoneCache = {
                            utc_offset_hours: offsetHours,
                            label: label,
                            description: description
                        };
                        console.log(`✓ Timezone config loaded: ${label} (UTC${offsetHours >= 0 ? '+' : ''}${offsetHours})`);
                    } else {
                        throw new Error('Failed to parse timezone from Candid');
                    }
                } else {
                    // Try data field as fallback
                    const config = response.data || response;
                    DiscoveryLogger.timezoneCache = config;
                    console.log(`✓ Timezone config loaded: ${config.label} (UTC${config.utc_offset_hours >= 0 ? '+' : ''}${config.utc_offset_hours})`);
                }
            } else {
                console.warn('⚠️ CanisterClient not available, using default timezone (AEDT +11)');
                // Set default if canister client not available
                DiscoveryLogger.timezoneCache = {
                    utc_offset_hours: 11,
                    label: 'AEDT',
                    description: 'Australian Eastern Daylight Time (UTC+11)'
                };
            }
        } catch (error: any) {
            console.error('❌ Failed to fetch timezone config:', error);
            // Fallback to AEDT if fetch fails
            DiscoveryLogger.timezoneCache = {
                utc_offset_hours: 11,
                label: 'AEDT',
                description: 'Australian Eastern Daylight Time (UTC+11)'
            };
        }
    }

    /**
     * Formats current timestamp using configured timezone from Timezone Configuration Service
     *
     * This function provides consistent timestamp formatting across the ASD system.
     * Used by both log display and file downloads to ensure uniform timestamps.
     * Matches the format used by the service map Rust canister.
     *
     * Architecture:
     * - Follows SOA principles by using centralized Timezone Configuration Service
     * - Uses cached timezone config to minimize service calls
     * - Falls back to AEDT (UTC+11) if cache not yet loaded
     *
     * @param forFilename - If true, uses hyphens instead of colons for filename compatibility
     * @returns ISO-formatted timestamp string with configured timezone
     * @private
     */
    private getAEDTTimestamp(forFilename: boolean = false): string {
        const now = new Date();

        // Get timezone config from cache (with fallback to AEDT)
        const config = DiscoveryLogger.timezoneCache || {
            utc_offset_hours: 11,
            label: 'AEDT',
            description: 'Australian Eastern Daylight Time (UTC+11)'
        };

        // Apply timezone offset
        const offsetMillis = config.utc_offset_hours * 60 * 60 * 1000;
        const adjustedTime = new Date(now.getTime() + offsetMillis);

        const year = adjustedTime.getUTCFullYear();
        const month = String(adjustedTime.getUTCMonth() + 1).padStart(2, '0');
        const day = String(adjustedTime.getUTCDate()).padStart(2, '0');
        const hours = String(adjustedTime.getUTCHours()).padStart(2, '0');
        const minutes = String(adjustedTime.getUTCMinutes()).padStart(2, '0');
        const seconds = String(adjustedTime.getUTCSeconds()).padStart(2, '0');
        const ms = String(adjustedTime.getUTCMilliseconds()).padStart(3, '0');

        if (forFilename) {
            // Format: YYYY-MM-DDTHH-mm-ss-mmm{TZ_LABEL} (hyphens for filesystem compatibility)
            return `${year}-${month}-${day}T${hours}-${minutes}-${seconds}-${ms}${config.label}`;
        } else {
            // Format: YYYY-MM-DDTHH:MM:SS.sss{TZ_LABEL} (standard display format, matches Rust canister)
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}${config.label}`;
        }
    }

    /**
     * Main logging function that creates and appends log entries
     *
     * Uses AEDT timestamps to match the service map logging format from the Rust canister.
     * This ensures consistent timestamp formatting across all ASD system logs.
     *
     * @param message - The message to log
     * @param type - The severity/type of the log message
     */
    private log(message: string, type: LogLevel = 'info'): void {
        if (!this.logElement) {
            console.warn('⚠️ Cannot log - discovery log element not available');
            return;
        }

        // Use AEDT timestamp format (matching service map logs from Rust canister)
        const timestamp: string = this.getAEDTTimestamp(false);
        const logEntry: HTMLDivElement = document.createElement('div');

        const colorClass: string = this.getColorClass(type);
        logEntry.className = colorClass;
        logEntry.textContent = `${timestamp} ${message}`;

        this.logElement.appendChild(logEntry);
        this.scrollToBottom();

        // Also log to console for debugging
        this.logToConsole(message, type);
    }

    /**
     * Gets the appropriate CSS color class for the given log type
     * @param type - The log level
     * @returns The Tailwind CSS class name for the log type
     * @private
     */
    private getColorClass(type: LogLevel): string {
        const colorMap: Record<LogLevel, string> = {
            'error': 'text-red-400',
            'warning': 'text-yellow-400',
            'success': 'text-green-400',
            'phase': 'text-cyan-400 font-bold',
            'progress': 'text-blue-400',
            'detail': 'text-gray-400',
            'info': 'text-green-400'
        };
        return colorMap[type] || 'text-green-400';
    }

    /**
     * Logs the message to the browser console with appropriate emoji and method
     * @param message - The message to log
     * @param type - The log level
     * @private
     */
    private logToConsole(message: string, type: LogLevel): void {
        const emoji: string = this.getEmoji(type);
        const consoleMethod: ConsoleMethod = type === 'error' ? 'error' :
                           type === 'warning' ? 'warn' : 'log';
        console[consoleMethod](`${emoji} ${message}`);
    }

    /**
     * Gets the emoji icon for the given log type
     * @param type - The log level
     * @returns The emoji string
     * @private
     */
    private getEmoji(type: LogLevel): string {
        const emojiMap: Record<LogLevel, string> = {
            'error': '❌',
            'warning': '⚠️',
            'success': '✅',
            'phase': '🔄',
            'progress': '📊',
            'detail': '📋',
            'info': 'ℹ️'
        };
        return emojiMap[type] || 'ℹ️';
    }

    /**
     * Shows the log container by removing the 'hidden' class
     */
    public show(): void {
        if (this.logContainer) {
            this.logContainer.classList.remove('hidden');
        }
    }

    /**
     * Hides the log container by adding the 'hidden' class
     */
    public hide(): void {
        if (this.logContainer) {
            this.logContainer.classList.add('hidden');
        }
    }

    /**
     * Clears all log entries from the display
     */
    public clear(): void {
        if (this.logElement) {
            this.logElement.innerHTML = '';
        }
    }

    /**
     * Scrolls the log container to show the most recent entries
     * @private
     */
    private scrollToBottom(): void {
        if (this.logElement) {
            this.logElement.scrollTop = this.logElement.scrollHeight;
        }
    }

    /**
     * Logs an error message
     * @param message - The error message to log
     */
    public logError(message: string): void {
        this.log(message, 'error');
    }

    /**
     * Logs a warning message
     * @param message - The warning message to log
     */
    public logWarning(message: string): void {
        this.log(message, 'warning');
    }

    /**
     * Logs a success message
     * @param message - The success message to log
     */
    public logSuccess(message: string): void {
        this.log(message, 'success');
    }

    /**
     * Logs a phase transition message
     * @param message - The phase message to log
     */
    public logPhase(message: string): void {
        this.log(message, 'phase');
    }

    /**
     * Logs a progress update message
     * @param message - The progress message to log
     */
    public logProgress(message: string): void {
        this.log(message, 'progress');
    }

    /**
     * Logs a detailed information message
     * @param message - The detail message to log
     */
    public logDetail(message: string): void {
        this.log(message, 'detail');
    }

    /**
     * Logs an informational message
     * @param message - The info message to log
     */
    public logInfo(message: string): void {
        this.log(message, 'info');
    }

    /**
     * Logs an empty line for spacing
     */
    public logSpacer(): void {
        this.log('', 'info');
    }

    /**
     * Starts a new discovery session by showing the log and logging a phase message
     * @param sessionName - The name of the discovery session
     */
    public startSession(sessionName: string): void {
        this.show();
        this.clear();
        this.logPhase(`🚀 ${sessionName} Started`);
    }

    /**
     * Ends a discovery session with success or failure status
     * @param sessionName - The name of the discovery session
     * @param success - Whether the session completed successfully (default: true)
     */
    public endSession(sessionName: string, success: boolean = true): void {
        if (success) {
            this.logSpacer();
            this.logSuccess(`🎉 ${sessionName} Completed Successfully!`);
        } else {
            this.logSpacer();
            this.logError(`❌ ${sessionName} Failed`);
        }
    }

    /**
     * Logs progress for batch operations with percentage calculation
     * @param current - Current number of items processed
     * @param total - Total number of items to process
     * @param description - Description of the items being processed (default: 'items')
     */
    public logBatchProgress(current: number, total: number, description: string = 'items'): void {
        const percentage: number = Math.round((current / total) * 100);
        this.logProgress(`📊 Progress: ${current}/${total} ${description} (${percentage}%)`);
    }

    /**
     * Logs the result of a device discovery attempt with detailed information
     * @param ip - The IP address of the device
     * @param result - The discovery result object containing status and inventory
     */
    public logDeviceResult(ip: string, result: DeviceResult): void {
        if (result.status === 'discovered') {
            this.logSuccess(`✅ Device ${ip} discovered successfully`);
            if (result.inventory?.hostname) {
                this.logDetail(`   📛 Hostname: ${result.inventory.hostname}`);
            }
            if (result.inventory?.accessible !== undefined) {
                this.logDetail(`   🔗 Accessible: ${result.inventory.accessible ? 'Yes' : 'No'}`);
            }
            if (result.inventory?.method) {
                this.logDetail(`   🔧 Method: ${result.inventory.method}`);
            }
        } else {
            this.logWarning(`❌ Device ${ip} is offline or unreachable`);
        }
    }

    /**
     * Refresh Timezone Configuration Cache
     * Following SOA principles - fetches latest timezone config from service
     * Should be called after user changes timezone settings
     *
     * @returns Promise resolving to updated timezone config
     */
    public async refreshTimezoneConfig(): Promise<TimezoneConfig> {
        try {
            if (typeof window !== 'undefined' && window.canisterClient) {
                const response = await window.canisterClient.getTimezoneConfig();

                // Parse from raw Candid string if data is empty (same logic as initializeTimezone)
                let offsetHours, label, description;
                if (response.raw) {
                    // Extract values from Candid string
                    const offsetMatch = response.raw.match(/utc_offset_hours\s*=\s*(-?\d+)/);
                    const labelMatch = response.raw.match(/label\s*=\s*"([^"]+)"/);
                    const descMatch = response.raw.match(/description\s*=\s*"([^"]+)"/);

                    if (offsetMatch && labelMatch) {
                        offsetHours = parseInt(offsetMatch[1], 10);
                        label = labelMatch[1];
                        description = descMatch ? descMatch[1] : `${label} timezone`;

                        DiscoveryLogger.timezoneCache = {
                            utc_offset_hours: offsetHours,
                            label: label,
                            description: description
                        };
                    } else {
                        throw new Error('Failed to parse timezone from Candid');
                    }
                } else {
                    // Try data field as fallback
                    const config = response.data || response;
                    DiscoveryLogger.timezoneCache = config;
                }

                const config = DiscoveryLogger.timezoneCache!;
                console.log(`✓ Timezone config refreshed: ${config.label} (UTC${config.utc_offset_hours >= 0 ? '+' : ''}${config.utc_offset_hours})`);
                this.logInfo(`Timezone updated to ${config.label} (UTC${config.utc_offset_hours >= 0 ? '+' : ''}${config.utc_offset_hours})`);
                return config;
            } else {
                throw new Error('CanisterClient not available');
            }
        } catch (error: any) {
            console.error('❌ Failed to refresh timezone config:', error);
            this.logError(`Failed to refresh timezone: ${error.message}`);
            throw error;
        }
    }

    /**
     * Downloads the current log as a text file
     */
    public downloadLog(): void {
        if (!this.logElement) {
            console.warn('Discovery log element not found');
            return;
        }

        // Get all log entries
        const logEntries: NodeListOf<HTMLDivElement> = this.logElement.querySelectorAll('div');
        if (logEntries.length === 0) {
            console.warn('No log entries to download');
            return;
        }

        // Build log content
        const timestamp: string = this.getAEDTTimestamp(true); // Use filename-safe format
        const logContent: string = Array.from(logEntries)
            .map(entry => entry.textContent)
            .join('\n');

        // Create download
        const blob: Blob = new Blob([logContent], { type: 'text/plain' });
        const url: string = URL.createObjectURL(blob);
        const a: HTMLAnchorElement = document.createElement('a');
        a.href = url;
        a.download = `discovery-log-${timestamp}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        console.log('📥 Discovery log downloaded successfully');
    }

    /**
     * Clears the log display and logs the action to console
     */
    public clearLog(): void {
        if (this.logElement) {
            this.logElement.innerHTML = '';
            console.log('🧹 Discovery log cleared');
        }
    }

    /**
     * Exports the current log as a plain text string
     * @returns The log content as a string, or empty string if no log element exists
     */
    public exportLog(): string {
        if (!this.logElement) return '';

        const entries: HTMLElement[] = Array.from(this.logElement.children) as HTMLElement[];
        return entries.map(entry => entry.textContent).join('\n');
    }

    /**
     * Saves the current log to a file with an optional custom filename
     * @param filename - Optional custom filename (default: auto-generated with timestamp)
     */
    public saveLogToFile(filename: string | null = null): void {
        const logContent: string = this.exportLog();
        if (!logContent) {
            this.logWarning('No log content to save');
            return;
        }

        const timestamp: string = this.getAEDTTimestamp(true); // Use filename-safe format
        const defaultFilename: string = `discovery-log-${timestamp}.txt`;
        const finalFilename: string = filename || defaultFilename;

        const blob: Blob = new Blob([logContent], { type: 'text/plain' });
        const url: string = URL.createObjectURL(blob);

        const link: HTMLAnchorElement = document.createElement('a');
        link.href = url;
        link.download = finalFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.logInfo(`💾 Log saved as ${finalFilename}`);
    }
}

// Create global instance
window.DiscoveryLogger = DiscoveryLogger;

/**
 * Register discovery logging window functions
 * Sprint 30: Using WindowFunctionRegistry for explicit ownership
 *
 * Owner: discovery-logging.ts (DiscoveryLogger class)
 */

// Register functions with explicit ownership
windowFunctionRegistry.registerModule('discovery-logging', {
    logDiscovery: {
        fn: function(message: string, type: LogLevel = 'info'): void {
            if (window.appCore?.getModule('discoveryLogger')) {
                window.appCore.getModule('discoveryLogger')?.log(message, type);
            } else if (window.discoveryLogger) {
                (window.discoveryLogger as any).log(message, type);
            } else {
                // Fallback to console
                console.log(`[${type.toUpperCase()}] ${message}`);
            }
        },
        description: 'Log a message to the discovery log display'
    },

    clearDiscoveryLog: {
        fn: function(): void {
            if (window.appCore?.getModule('discoveryLogger')) {
                window.appCore.getModule('discoveryLogger')?.clearLog();
            } else if (window.discoveryLogger) {
                window.discoveryLogger.clearLog();
            }
        },
        description: 'Clear all entries from the discovery log'
    },

    downloadDiscoveryLog: {
        fn: function(): void {
            const logger: DiscoveryLogger | undefined = window.appCore?.getModule('discoveryLogger');
            if (logger && logger.downloadLog) {
                logger.downloadLog();
            } else if (window.discoveryLogger && window.discoveryLogger.downloadLog) {
                window.discoveryLogger.downloadLog();
            } else {
                console.warn('Discovery logger not available for download');
            }
        },
        description: 'Download the discovery log as a text file'
    }
});

